import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, isEndpointUnavailable } from "../api/client";
import type { TaskSummary } from "../api/types";
import { taskIsActive } from "../utils/format";

interface TaskFeedContextValue {
  tasks: TaskSummary[];
  activeTasks: TaskSummary[];
  loading: boolean;
  error: string;
  unsupported: boolean;
  lastUpdated: Date | null;
  refresh: () => void;
}

const TaskFeedContext = createContext<TaskFeedContextValue | null>(null);

export function TaskFeedProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unsupported, setUnsupported] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let disposed = false;
    let timer = 0;
    let inFlight = false;

    const schedule = (delay: number) => {
      window.clearTimeout(timer);
      if (!disposed) timer = window.setTimeout(load, delay);
    };

    const load = async () => {
      if (disposed || inFlight) return;
      if (document.visibilityState === "hidden") {
        schedule(20_000);
        return;
      }
      inFlight = true;
      try {
        const result = await api.tasks.list();
        if (disposed) return;
        setTasks(result.items || []);
        setUnsupported(false);
        setError("");
        setLastUpdated(new Date());
        schedule(result.items.some(taskIsActive) ? 3_000 : 15_000);
      } catch (reason) {
        if (disposed) return;
        if (isEndpointUnavailable(reason)) {
          setUnsupported(true);
          setTasks([]);
        } else {
          setError(reason instanceof Error ? reason.message : "任务状态加载失败");
          schedule(15_000);
        }
      } finally {
        if (!disposed) {
          setLoading(false);
          inFlight = false;
        }
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        window.clearTimeout(timer);
        void load();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    void load();
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [revision]);

  const activeTasks = tasks.filter(taskIsActive);
  return (
    <TaskFeedContext.Provider
      value={{
        tasks,
        activeTasks,
        loading,
        error,
        unsupported,
        lastUpdated,
        refresh: () => setRevision((value) => value + 1),
      }}
    >
      {children}
    </TaskFeedContext.Provider>
  );
}

export function useTaskFeed() {
  const value = useContext(TaskFeedContext);
  if (!value) throw new Error("useTaskFeed must be used inside TaskFeedProvider");
  return value;
}
